// src/utils/blogUtils-new.ts
// Version optimisée avec styles inline pour garantir le bon affichage

import { Article, ArticleCard } from '../types/Article';

// Resolve static assets to built URLs (works in prod)
const assetMap = import.meta.glob('/src/assets/**/*.{png,jpg,jpeg,webp,avif,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

function resolveAsset(input?: string): string {
  if (!input) return '';
  // Normalize to absolute /src path
  let key = input;
  if (!key.startsWith('/src/')) {
    key = key.startsWith('src/') ? `/${key}` : `/src/${key.replace(/^\//, '')}`;
  }
  return assetMap[key] || input;
}

export class BlogUtils {
    /**
     * Crée un slug à partir du titre de l'article
     * @param title Titre de l'article
     * @returns Slug normalisé
     */
    createSlug(title: string): string {
        return title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
            .replace(/[^\w\s-]/g, '') // Supprime les caractères spéciaux
            .replace(/\s+/g, '-') // Remplace les espaces par des tirets
            .replace(/-+/g, '-'); // Évite les tirets multiples
    }

    /**
     * Crée une carte d'article à partir d'un article complet
     * @param article Article complet
     * @returns ArticleCard pour l'affichage dans les listes
     */
    createArticleCard(article: Article): ArticleCard {
        return {
            title: article.title,
            date: article.date,
            excerpt: article.excerpt,
            image: resolveAsset(article.image) || resolveAsset('/src/assets/optimized/AccueilWONNew-800w.webp'),
            slug: this.createSlug(article.title)
        };
    }

    /**
     * Expose l'utilitaire de résolution d'assets pour les autres modules
     */
    resolveAssetPath(input?: string): string {
        return resolveAsset(input);
    }

    /**
     * Remplace les chemins relatifs (ex: /src/...) dans le contenu HTML par des URLs résolues
     */
    resolveContentAssets(html?: string): string {
        if (!html) return html || '';

        return html.replace(/(src|data-src)=(\"|\')([^\"\']+)(\"|\')/gi, (full, attr, quoteOpen, value, quoteClose) => {
            const source = value.trim();

            // Ignorer les URLs absolues ou les données inline
            if (!source || /^https?:/i.test(source) || source.startsWith('data:')) {
                return full;
            }

            const resolved = resolveAsset(source);
            const closingQuote = quoteClose || quoteOpen;
            return `${attr}=${quoteOpen}${resolved}${closingQuote}`;
        });
    }

    /**
     * Rendu HTML pour l'article en vedette
     * @param article Carte d'article
     * @returns HTML de l'article en vedette
     */
    renderFeaturedArticle(article: ArticleCard): string {
        const imageUrl = resolveAsset(article.image) || resolveAsset('/src/assets/optimized/AccueilWONNew-800w.webp');

        return `
            <a href="single-article.html?slug=${article.slug}" 
               style="display: block; height: 100%; background: rgba(255, 255, 255, 0.05); 
                      border-radius: 12px; overflow: hidden; box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1); 
                      transition: all 0.3s ease; text-decoration: none;">
                <img src="${imageUrl}" alt="Illustration de l'article ${article.title}" 
                     style="width: 100%; height: 350px; object-fit: cover; display: block;"
                     onerror="this.onerror=null;this.src='${resolveAsset('/src/assets/logo-final.png')}';">
                <time style="display: block; margin-top: 1.5rem; margin-left: 1.5rem; color: #3498db;">${article.date}</time>
                <h3 style="font-size: 1.5rem; font-weight: 700; margin: 0.75rem 1.5rem; color: #ecf0f1;">${article.title}</h3>
                <p style="margin: 0.75rem 1.5rem 1.5rem; line-height: 1.5; color: #ecf0f1;">${article.excerpt}</p>
                <span style="display: inline-flex; align-items: center; margin-left: 1.5rem; 
                           margin-bottom: 1.5rem; color: #3498db; font-weight: 500;">
                    Lire la suite
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" 
                         style="width: 1rem; height: 1rem; margin-left: 0.25rem;">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                    </svg>
                </span>
            </a>
        `;
    }

    /**
     * Rendu HTML pour une carte d'article récent
     * @param card Carte d'article
     * @returns HTML de la carte d'article
     */
    renderRecentCard(card: ArticleCard): string {
        return `
            <a href="single-article.html?slug=${card.slug}" 
               style="display: flex; gap: 1.25rem; background: rgba(255, 255, 255, 0.05); 
                      border-radius: 8px; padding: 1rem; transition: all 0.3s ease; 
                      height: auto; text-decoration: none;">
                <div style="width: 100px; height: 100px; flex-shrink: 0; border-radius: 8px; overflow: hidden;">
                    <img src="${resolveAsset(card.image)}" alt="Illustration de l'article ${card.title}" 
                         style="width: 100%; height: 100%; object-fit: cover;" />
                </div>
                <div style="flex-grow: 1; display: flex; flex-direction: column;">
                    <time style="display: block; color: #3498db; font-size: 0.9rem; margin-bottom: 0.5rem;">${card.date}</time>
                    <h3 style="font-size: 1.1rem; font-weight: 600; color: #ecf0f1; margin-bottom: 0.5rem; line-height: 1.4;">${card.title}</h3>
                    <span style="color: #3498db; font-size: 0.9rem; display: inline-flex; align-items: center;">
                        Lire la suite
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" 
                             style="width: 0.8rem; height: 0.8rem; margin-left: 0.25rem;">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                        </svg>
                    </span>
                </div>
            </a>`;
    }

    /**
     * Rendu HTML immersif pour une carte d'article
     * @param card Carte d'article
     * @returns HTML immersif de la carte d'article
     */
    renderImmersiveCard(card: ArticleCard): string {
        return `
            <a href="single-article.html?slug=${card.slug}" 
               style="display: block; background: rgba(255, 255, 255, 0.05); border-radius: 12px; 
                      overflow: hidden; box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1); 
                      transition: transform 0.3s ease, box-shadow 0.3s ease; text-decoration: none;">
                <div style="position: relative;">
                    <img src="${card.image}" alt="Illustration de l'article ${card.title}" 
                         style="width: 100%; height: 200px; object-fit: cover; display: block;" />
                    <div style="position: absolute; bottom: 0; left: 0; right: 0; 
                                background: linear-gradient(to top, rgba(19, 19, 19, 0.8), transparent); 
                                padding: 1.5rem 1rem 0.5rem;">
                        <time style="color: #3498db; font-size: 0.85rem;">${card.date}</time>
                    </div>
                </div>
                <div style="padding: 1rem;">
                    <h3 style="font-size: 1.2rem; font-weight: 600; color: #ecf0f1; margin-bottom: 0.75rem; 
                               line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; 
                               -webkit-box-orient: vertical; overflow: hidden;">${card.title}</h3>
                    <p style="font-size: 0.95rem; color: #bdc3c7; margin-bottom: 1rem; line-height: 1.5; 
                              display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; 
                              overflow: hidden;">${card.excerpt}</p>
                    <span style="color: #3498db; font-size: 0.9rem; font-weight: 500; 
                                display: inline-flex; align-items: center;">
                        Lire la suite
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" 
                             style="width: 0.8rem; height: 0.8rem; margin-left: 0.25rem;">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                        </svg>
                    </span>
                </div>
            </a>
        `;
    }

    /**
     * Rendu HTML pour une carte d'article dans Swiper
     * @param article Carte d'article
     * @returns HTML de la carte d'article pour Swiper
     */
    renderArticleCard(article: ArticleCard): string {
        const imageUrl = article.image || "/src/assets/optimized/AccueilWONNew-800w.webp";

        return `
            <a href="single-article.html?slug=${article.slug}"
               style="display: block; background: rgba(255, 255, 255, 0.05); border-radius: 12px; 
                      overflow: hidden; box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1); 
                      transition: all 0.3s ease; text-decoration: none; height: 100%;">
                <div style="aspect-ratio: 16/9; overflow: hidden;">
                    <img src="${imageUrl}" alt="Illustration de l'article ${article.title}"
                         style="width: 100%; height: 100%; object-fit: cover;">
                </div>
                <div style="padding: 1rem;">
                    <time style="font-size: 0.85rem; color: #3498db;">${article.date}</time>
                    <h3 style="font-size: 1.25rem; font-weight: 600; margin: 0.5rem 0; 
                              background: linear-gradient(90deg, #ed4c67, #3498db); -webkit-background-clip: text; 
                              background-clip: text; color: transparent; display: inline-block;">${article.title}</h3>
                    <p style="font-size: 0.9rem; color: #ecf0f1; margin: 0.5rem 0 1rem; 
                             display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; 
                             overflow: hidden;">${article.excerpt}</p>
                    <span style="display: inline-flex; align-items: center; color: #ed4c67; 
                                font-weight: 500; font-size: 0.9rem;">
                        Lire la suite
                        <svg style="width: 0.8rem; height: 0.8rem; margin-left: 0.25rem;" 
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                        </svg>
                    </span>
                </div>
            </a>
        `;
    }

    /**
     * Rendu HTML immersif pour une carte d'article (version moderne)
     * @param card Carte d'article
     * @param category Catégorie de l'article
     * @param readTime Temps de lecture
     * @returns HTML immersif de la carte d'article modernisé
     */
    renderModernCard(card: ArticleCard, category: string, readTime: number = 5): string {
        return `
            <div class="modern-blog-card" data-category="${category}" style="height: 100%; background-color: rgba(20, 30, 40, 0.3); border-radius: 12px; overflow: hidden; display: flex; flex-direction: column;">
                <a href="single-article.html?slug=${card.slug}" 
                   style="display: flex; flex-direction: column; text-decoration: none; height: 100%;">
                    <div style="position: relative; overflow: hidden; border-radius: 12px 12px 0 0;">
                        <img src="${resolveAsset(card.image)}" alt="Illustration de l'article ${card.title}" 
                            style="width: 100%; height: 220px; object-fit: cover; display: block; transition: transform 0.5s ease;" />
                        <span style="position: absolute; top: 12px; right: 12px; background-color: #e67e22; color: white; 
                                     font-size: 0.75rem; font-weight: 600; padding: 0.25rem 0.75rem; border-radius: 20px; 
                                     box-shadow: 0 4px 10px rgba(230, 126, 34, 0.3); z-index: 2;">${category}</span>
                    </div>
                    <div style="padding: 1.25rem; flex-grow: 1; display: flex; flex-direction: column;">
                        <div style="display: flex; align-items: center; margin-bottom: 0.75rem; font-size: 0.875rem; color: #95a5a6;">
                            <div style="display: flex; align-items: center; margin-right: 1rem;">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" stroke-width="2" 
                                    style="width: 14px; height: 14px; margin-right: 0.25rem;">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                ${card.date}
                            </div>
                            <div style="display: flex; align-items: center;">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" stroke-width="2" 
                                    style="width: 14px; height: 14px; margin-right: 0.25rem;">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                ${readTime} min
                            </div>
                        </div>
                        <h3 style="font-size: 1.25rem; font-weight: 700; color: #ecf0f1; margin-bottom: 0.75rem; 
                                  line-height: 1.4; transition: color 0.3s ease;">${card.title}</h3>
                        <p style="color: #bdc3c7; font-size: 0.9375rem; line-height: 1.6; margin-bottom: 1.5rem; flex-grow: 1;">${card.excerpt}</p>
                        <div style="display: flex; justify-content: flex-end; align-items: flex-end;">
                            <div style="display: inline-flex; align-items: center; font-size: 0.9375rem; 
                                       font-weight: 600; color: #3498db; transition: color 0.3s ease;">
                                Lire la suite
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" 
                                    style="width: 16px; height: 16px; margin-left: 0.375rem; transition: transform 0.3s ease;">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        `;
    }
}
